import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { originStateFrom } from '@/shared/lib/origin';

import {
  type DatabaseTable,
  identityStyle,
  usePaletteOptions,
  useCreateTable,
  useDeleteTable,
  useMoveTable,
  useRenameTable,
} from '@/entities/database';
import { useErrorMessage } from '@/shared/i18n';
import { prefersReducedMotion, useIsMobile } from '@/shared/lib/media';
import {
  Button,
  Combobox,
  ComboboxList,
  type ComboboxOption,
  ConfirmDialog,
  Dialog,
  IconButton,
  Icons,
  MenuItem,
  MenuList,
  MenuRow,
  PalettePicker,
  Popover,
  TextField,
  useToast,
} from '@/shared/ui';

import { DuplicateTableDialog } from './DuplicateTableDialog';
import { type MoveAnchor, TabSlot } from './TabSlot';
import styles from './TableTabs.module.css';

const NAME_MAX = 100;

function NameDialog({
  title,
  initial,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  title: string;
  initial: string;
  pending: boolean;
  error: unknown;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [name, setName] = useState(initial);
  return (
    <Dialog title={title} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) onSubmit(name.trim());
        }}
      >
        <TextField
          label={t('databases.tableName')}
          value={name}
          maxLength={NAME_MAX}
          autoFocus
          error={error ? message(error) : undefined}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={pending || !name.trim()}>
            {t('common.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** The color of a table: a palette choice that only marks its tab. */
function ColorDialog({
  table,
  pending,
  error,
  onPick,
  onClose,
}: {
  table: DatabaseTable;
  pending: boolean;
  error: unknown;
  onPick: (color: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const options = usePaletteOptions();
  return (
    <Dialog title={t('catalog.tableColorTitle', { name: table.name })} onClose={onClose}>
      <div className={styles.form}>
        <PalettePicker
          label={t('catalog.tableColor')}
          value={table.color}
          options={options}
          noneLabel={t('databases.palette.none')}
          custom
          disabled={pending}
          onPick={onPick}
        />
        <p className={styles.hint}>{t('catalog.tableColorHint')}</p>
        {error ? (
          <p role="alert" className={styles.hint}>
            {message(error)}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Space kept around the active tab when it is scrolled into view. */
const REVEAL_MARGIN = 24;

/** Scrolls ONLY the tab row (never the page) so `tab` is fully visible with some room around it. */
function revealTab(list: HTMLElement, tab: HTMLElement) {
  // Rectangles, not offsetLeft: the tab's offset parent is not the (unpositioned) row.
  const rowRect = list.getBoundingClientRect();
  const tabRect = tab.getBoundingClientRect();
  const left = tabRect.left - rowRect.left + list.scrollLeft;
  const right = left + tabRect.width;
  let target: number | null = null;
  if (left - REVEAL_MARGIN < list.scrollLeft) target = Math.max(0, left - REVEAL_MARGIN);
  else if (right + REVEAL_MARGIN > list.scrollLeft + list.clientWidth) {
    target = right + REVEAL_MARGIN - list.clientWidth;
  }
  if (target === null || Math.abs(target - list.scrollLeft) < 1) return;
  if (typeof list.scrollTo === 'function') {
    list.scrollTo({ left: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  } else list.scrollLeft = target;
}

interface Props {
  databaseId: string;
  tables: readonly DatabaseTable[];
  activeId: string | null;
  onSelect: (tableId: string) => void;
  canStructure: boolean;
  /** Export the table to a file (offered when the viewer may; the dialog is the page's). */
  onExportTable?: (table: DatabaseTable) => void;
  /** Import a table from a file into this database. */
  onImportTable?: () => void;
}

/**
 * The sheets of a database as horizontal tabs (they scroll inside their own row), an "all
 * tables" list for long databases and - for those who manage the structure - add, rename,
 * reorder and delete. Arrow keys move between tabs.
 */
export function TableTabs({
  databaseId,
  tables,
  activeId,
  onSelect,
  canStructure,
  onExportTable,
  onImportTable,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const message = useErrorMessage();
  const create = useCreateTable(databaseId);
  const rename = useRenameTable(databaseId);
  const move = useMoveTable(databaseId);
  const remove = useDeleteTable(databaseId);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<DatabaseTable | null>(null);
  const [coloring, setColoring] = useState<DatabaseTable | null>(null);
  const [deleting, setDeleting] = useState<DatabaseTable | null>(null);
  const [duplicating, setDuplicating] = useState<DatabaseTable | null>(null);
  const mobile = useIsMobile();
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const dropBeside = useCallback(
    (sourceId: string, anchor: MoveAnchor) => {
      move.mutate(
        { id: sourceId, ...anchor },
        {
          onError: (error) => {
            toast.show({ message: message(error) });
          },
        },
      );
    },
    [move, toast, message],
  );
  const listRef = useRef<HTMLDivElement>(null);

  const focusTab = (index: number) => {
    const tabs = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    const tab = tabs?.[index];
    if (tab) tab.focus({ preventScroll: true }); // the effect below reveals the selected tab
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tables.length - 1;
    const target =
      event.key === 'ArrowRight'
        ? Math.min(index + 1, last)
        : event.key === 'ArrowLeft'
          ? Math.max(index - 1, 0)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (target === null) return;
    event.preventDefault();
    focusTab(target);
    const table = tables[target];
    if (table) onSelect(table.id);
  };

  const options: ComboboxOption[] = tables.map((table) => ({
    value: table.id,
    label: table.name,
    ...(table.color && {
      leading: (
        <span
          className={styles.dot}
          style={identityStyle(table.color, '--table-color')}
          aria-hidden
        />
      ),
    }),
  }));

  // How much of the tab row is hidden on each side (from its real size and scroll position).
  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const next = {
      left: list.scrollLeft > 1,
      right: list.scrollLeft + list.clientWidth < list.scrollWidth - 1,
    };
    setOverflow((current) =>
      current.left === next.left && current.right === next.right ? current : next,
    );
  }, []);
  const names = tables.map((table) => table.name).join('\n');
  useEffect(() => {
    const list = listRef.current;
    if (!list || mobile) return;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            // the row got narrower or wider (the scroll buttons appear, the window changes): keep the
            // selected tab in view
            const active = list.querySelector<HTMLElement>('[data-active]');
            if (active) revealTab(list, active);
            measure();
          });
    observer?.observe(list);
    list.addEventListener('scroll', measure, { passive: true });
    measure();
    return () => {
      observer?.disconnect();
      list.removeEventListener('scroll', measure);
    };
  }, [measure, mobile, names]);
  // The selected table stays in view: on load, a link, back / forward, "All tables", a new or
  // duplicated table. Nothing moves when it is already fully visible, and focus is never taken.
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>('[data-active]');
    if (list && active) {
      revealTab(list, active);
      measure();
    }
  }, [activeId, names, mobile, measure]);
  const scrollTabs = (direction: -1 | 1) => {
    const list = listRef.current;
    if (!list) return;
    const left = list.scrollLeft + direction * Math.max(120, list.clientWidth * 0.8);
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } else list.scrollLeft = left;
  };

  // The actions of one table (rename, color, move, duplicate, export, delete): the same list for
  // the active tab and for the compact selector of a phone.
  const tableMenu = (table: DatabaseTable, index: number, close: () => void) => (
    <MenuList>
      <MenuRow>
        <MenuItem
          icon={<Icons.edit />}
          onClick={() => {
            close();
            setRenaming(table);
          }}
        >
          {t('databases.renameTable')}
        </MenuItem>
      </MenuRow>
      <MenuRow>
        <MenuItem
          icon={<Icons.palette />}
          onClick={() => {
            close();
            setColoring(table);
          }}
        >
          {t('catalog.tableColor')}
        </MenuItem>
      </MenuRow>
      {index > 0 && (
        <MenuRow>
          <MenuItem
            icon={<Icons.chevronLeft />}
            onClick={() => {
              close();
              const previous = tables[index - 1];
              if (previous) move.mutate({ id: table.id, before_id: previous.id });
            }}
          >
            {t('databases.moveLeft')}
          </MenuItem>
        </MenuRow>
      )}
      {index < tables.length - 1 && (
        <MenuRow>
          <MenuItem
            icon={<Icons.chevronRight />}
            onClick={() => {
              close();
              const next = tables[index + 1];
              if (next) move.mutate({ id: table.id, after_id: next.id });
            }}
          >
            {t('databases.moveRight')}
          </MenuItem>
        </MenuRow>
      )}
      <MenuRow>
        <MenuItem
          icon={<Icons.copy />}
          onClick={() => {
            close();
            setDuplicating(table);
          }}
        >
          {t('databases.duplicate.table.action')}
        </MenuItem>
      </MenuRow>
      {onExportTable && (
        <MenuRow>
          <MenuItem
            icon={<Icons.export />}
            onClick={() => {
              close();
              onExportTable(table);
            }}
          >
            {t('databases.portability.export.tableAction')}
          </MenuItem>
        </MenuRow>
      )}
      <MenuRow separated>
        <MenuItem
          icon={<Icons.trash />}
          danger
          onClick={() => {
            close();
            setDeleting(table);
          }}
        >
          {t('databases.deleteTable')}
        </MenuItem>
      </MenuRow>
    </MenuList>
  );

  const active = tables.find((table) => table.id === activeId);
  const activeIndex = active ? tables.indexOf(active) : -1;

  return (
    <div className={styles.bar} data-mobile={mobile || undefined}>
      {mobile && tables.length > 0 && (
        <div className={styles.selector} data-testid="table-selector">
          <Combobox
            label={t('databases.currentTable')}
            value={activeId}
            options={options}
            searchPlaceholder={t('databases.searchTables')}
            emptyText={t('databases.noTablesFound')}
            onChange={onSelect}
          />
        </div>
      )}
      {mobile && canStructure && active && (
        <Popover
          label={t('databases.tableMenu', { name: active.name })}
          width={260}
          alignEnd
          trigger={(props) => (
            <button
              type="button"
              {...props}
              className={styles.tabMenu}
              aria-label={t('databases.tableMenu', { name: active.name })}
            >
              <Icons.more />
            </button>
          )}
        >
          {(close) => tableMenu(active, activeIndex, close)}
        </Popover>
      )}
      {!mobile && (overflow.left || overflow.right) && (
        <IconButton
          label={t('databases.tabsLeft')}
          icon={<Icons.chevronLeft />}
          disabled={!overflow.left}
          onClick={() => {
            scrollTabs(-1);
          }}
        />
      )}
      {!mobile && (
        <div
          ref={listRef}
          className={styles.tabs}
          role="tablist"
          aria-label={t('databases.tables')}
          data-testid="table-tabs"
        >
          {tables.map((table, index) => {
            const active = table.id === activeId;
            return (
              <TabSlot
                key={table.id}
                databaseId={databaseId}
                tableId={table.id}
                previousId={tables[index - 1]?.id}
                nextId={tables[index + 1]?.id}
                enabled={canStructure}
                onDropBeside={dropBeside}
                className={styles.tabWrap}
                data-active={active || undefined}
                data-color={table.color ?? undefined}
                {...(table.color && { style: identityStyle(table.color, '--table-color') })}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={active}
                  tabIndex={active ? 0 : -1}
                  className={styles.tab}
                  title={table.name}
                  onClick={() => {
                    onSelect(table.id);
                  }}
                  onKeyDown={(event) => {
                    onKeyDown(event, index);
                  }}
                >
                  {table.color && <span className={styles.dot} aria-hidden />}
                  {table.name}
                </button>
                {active && canStructure && (
                  <Popover
                    label={t('databases.tableMenu', { name: table.name })}
                    width={240}
                    trigger={(props) => (
                      <button
                        type="button"
                        {...props}
                        className={styles.tabMenu}
                        aria-label={t('databases.tableMenu', { name: table.name })}
                      >
                        <Icons.chevronDown />
                      </button>
                    )}
                  >
                    {(close) => tableMenu(table, index, close)}
                  </Popover>
                )}
              </TabSlot>
            );
          })}
        </div>
      )}
      {!mobile && (overflow.left || overflow.right) && (
        <IconButton
          label={t('databases.tabsRight')}
          icon={<Icons.chevronRight />}
          disabled={!overflow.right}
          onClick={() => {
            scrollTabs(1);
          }}
        />
      )}
      {!mobile && tables.length > 1 && (
        <Popover
          label={t('databases.allTables')}
          width={300}
          alignEnd
          trigger={(props) => (
            <button
              type="button"
              {...props}
              className={styles.allTables}
              aria-label={t('databases.allTables')}
              title={t('databases.allTables')}
            >
              <Icons.list />
              <span className={styles.count}>{tables.length}</span>
            </button>
          )}
        >
          {(close) => (
            <ComboboxList
              label={t('databases.allTables')}
              options={options}
              value={activeId}
              searchPlaceholder={t('databases.searchTables')}
              emptyText={t('databases.noTablesFound')}
              limit={Math.max(100, tables.length)}
              searchable
              onPick={(id) => {
                close();
                onSelect(id);
              }}
            />
          )}
        </Popover>
      )}
      {canStructure && onImportTable && (
        <IconButton
          label={t('databases.portability.import.tableAction')}
          icon={<Icons.import />}
          onClick={onImportTable}
        />
      )}
      {canStructure && (
        <IconButton
          label={t('databases.addTable')}
          icon={<Icons.plus />}
          onClick={() => {
            setAdding(true);
          }}
        />
      )}

      {duplicating && (
        <DuplicateTableDialog
          table={duplicating}
          onClose={() => {
            setDuplicating(null);
          }}
          onDuplicated={(copy, report) => {
            setDuplicating(null);
            toast.show({
              message:
                t('databases.duplicate.table.done', { name: copy.name }) +
                (report.dropped_user_references > 0
                  ? ` ${t('databases.duplicate.droppedPeople', { count: report.dropped_user_references })}`
                  : ''),
            });
            if (copy.database_id === databaseId) onSelect(copy.id);
            else {
              void navigate(`/app/databases/${copy.database_id}/${copy.id}`, {
                state: originStateFrom(location),
              });
            }
          }}
        />
      )}
      {adding && (
        <NameDialog
          title={t('databases.addTable')}
          initial={t('databases.defaultTableName', { index: tables.length + 1 })}
          pending={create.isPending}
          error={create.error}
          onClose={() => {
            create.reset();
            setAdding(false);
          }}
          onSubmit={(name) => {
            create.mutate(name, {
              onSuccess: (table) => {
                setAdding(false);
                onSelect(table.id);
              },
            });
          }}
        />
      )}
      {renaming && (
        <NameDialog
          title={t('databases.renameTable')}
          initial={renaming.name}
          pending={rename.isPending}
          error={rename.error}
          onClose={() => {
            rename.reset();
            setRenaming(null);
          }}
          onSubmit={(name) => {
            rename.mutate(
              { id: renaming.id, name },
              {
                onSuccess: () => {
                  setRenaming(null);
                },
              },
            );
          }}
        />
      )}
      {coloring && (
        <ColorDialog
          table={coloring}
          pending={rename.isPending}
          error={rename.error}
          onClose={() => {
            rename.reset();
            setColoring(null);
          }}
          onPick={(color) => {
            rename.mutate(
              { id: coloring.id, color },
              {
                onSuccess: () => {
                  setColoring(null);
                },
              },
            );
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t('databases.deleteTableTitle')}
          message={t('databases.deleteTableMessage', { name: deleting.name })}
          confirmLabel={t('databases.deleteTable')}
          pending={remove.isPending}
          onCancel={() => {
            setDeleting(null);
          }}
          onConfirm={() => {
            const gone = deleting;
            remove.mutate(gone.id, {
              onSettled: () => {
                setDeleting(null);
              },
              onSuccess: () => {
                const next = tables.find((x) => x.id !== gone.id);
                if (next && gone.id === activeId) onSelect(next.id);
              },
              onError: (error) => {
                toast.show({ message: message(error) });
              },
            });
          }}
        />
      )}
    </div>
  );
}
